import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { capitalize } from "lodash";
import { communityTabs } from "./data";
import mediaQueries from "styles/utils/mediaQueries";
import DisplayNews from "./components/DisplayNews";
import DisplayPromos from "./components/DisplayPromos";
import DisplayOffers from "./components/DisplayOffers";
import DisplayEvents from "./components/DisplayEvents";
import DisplayForums from "./components/DisplayForums";
import DisplayBlogposts from "./components/DisplayBlogposts";
import { CommunityContentsProps } from "app/views/Admin/Community/data";
import media, { mediaObj } from "styles/utils/media";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  cardVariant,
  slideIn,
  staggerContainer,
} from "app/utils/animationVariants";
import { useMobileCheck } from "app/hooks/useMobileCheck";

const Community = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeParam = searchParams.get("activeTab");
  const [selectedCategory, setSelectedCategory] = useState<number>(1);
  const isMobile = useMobileCheck();

  useEffect(() => {
    !activeParam && setSearchParams({ activeTab: communityTabs[0].name });
  }, [setSearchParams, activeParam]);

  useEffect(() => {
    if (activeParam) {
      const result = communityTabs.filter((ev) => ev.name === activeParam);
      setSelectedCategory(result[0].id);
    }
  }, [activeParam]);

  return (
    <Container>
      <Header initial={"initial"} animate={"animate"}>
        <HeaderWrapper variants={slideIn()} custom={2}>
          <h5>Adbond Community</h5>
        </HeaderWrapper>

        <Para variants={slideIn("right")} custom={2.5}>
          Here, you can access tips, insights and updates about land,
          agriculture, home development projects and industry trends. To learn
          more about Adbond, visit <br />
          <Anchor href="https://www.adbondharvestandhomes.com">
            https://www.adbondharvestandhomes.com
          </Anchor>{" "}
        </Para>
      </Header>

      <TabsWrapper
        variants={staggerContainer}
        custom={isMobile ? 0.8 : 0.5}
        viewport={{ once: true, amount: isMobile ? 0 : 0.5 }}
        whileInView={"animate"}
        initial={"initial"}
      >
        {communityTabs.map((x, i) => (
          <Tab
            key={i}
            isActive={x.id === selectedCategory}
            onClick={() => {
              setSelectedCategory(x.id);
              setSearchParams({ activeTab: x.name });
            }}
            variants={cardVariant}
          >
            {capitalize(x.name)}
          </Tab>
        ))}
      </TabsWrapper>
      <CardWrapper>
        {selectedCategory === CommunityContentsProps.news && <DisplayNews />}
        {selectedCategory === CommunityContentsProps.promo && <DisplayPromos />}
        {selectedCategory === CommunityContentsProps.offers && (
          <DisplayOffers />
        )}
        {selectedCategory === CommunityContentsProps.events && (
          <DisplayEvents />
        )}
        {selectedCategory === CommunityContentsProps.forum && <DisplayForums />}
        {selectedCategory === CommunityContentsProps.blogPost && (
          <DisplayBlogposts />
        )}
      </CardWrapper>
    </Container>
  );
};

export default Community;

const Container = styled.div`
  padding: 20px 24px;
  min-height: 80vh;

  width: 80%;
  margin: 8rem auto 4.25rem;

  ${media.mediumDesktop`
  width: 100%;
  `}

  ${media.smallDesktop`
  grid-template-columns: 1fr;
  /* width: 80%; */
  margin-left: unset;
        margin-right: unset;
        margin-top: 6rem;
        `}


    ${media.bigMobile`
        margin-left: unset;
        margin-right: unset;
        margin-top: 6rem;
        width: 100%;
        `} 
        
    ${mediaQueries.mobile} {
    padding: 0;
    background: transparent;
  }
`;

const Header = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;
  margin-bottom: 40px;

  ${mediaObj.smallDesktop} {
    width: 100%;
  }

  ${mediaQueries.mobile} {
    gap: 14px;
  }
`;

const HeaderWrapper = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: space-between;

  h5 {
    font-weight: 500;
    font-size: 28px;
    line-height: 140%;
    color: #181d0b;
    text-align: center;
  }

  ${mediaQueries.mobile} {
    h5 {
      font-size: 22px;
    }
  }
`;

const Para = styled(motion.div)`
  font-weight: 450;
  font-size: 1.2rem;
  line-height: 140%;
  color: #5e5e5e;
  text-align: center;

  ${mediaQueries.mobile} {
    font-size: 16px;
  }
`;

const TabsWrapper = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 40px;
  margin: 40px 0;

  ${mediaObj.smallDesktop} {
    justify-content: flex-start;
    overflow-x: scroll;
  }

  ${mediaQueries.mobile} {
    gap: 10px;
    width: 100%;
    margin: 25px 0;
    padding-bottom: 5px;
    overflow-x: scroll;
  }
`;

interface tabProps {
  isActive?: boolean;
}

const Tab = styled(motion.span)<tabProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  padding: 8px 16px;
  height: 38px;
  font-weight: 450;
  font-size: 1rem;
  color: ${({ isActive }) => (isActive ? "#FBFBFB" : "#535353")};
  background: ${({ isActive }) => (isActive ? "#464646" : "#E2E2E2")};
  cursor: pointer;
  transition: all 0.2s ease-in;
  white-space: nowrap;
`;

const CardWrapper = styled.div`
  display: flex;
  gap: 24px;
  flex-direction: column;

  background: #ffffff;
  box-shadow: 0px 0px 1px rgba(66, 71, 76, 0.32), 0px 8px 48px #eeeeee;
  border-radius: 20px;
  padding: 20px 24px;

  ${mediaQueries.mobile} {
    padding: 0;
    background: transparent;
    box-shadow: none;
  }
`;

const Anchor = styled.a`
  font-weight: 450;
  font-size: 1rem;
  line-height: 140%;
  color: #afd34f;
  word-break: break-all;

  &:hover {
    text-decoration: underline;
    transition: all 0.3s ease-in-out;
  }
`;
